import React, {useState} from 'react';

import CloseIcon from '@mui/icons-material/Close';
import PermissionsModal from './PermissionsModal';
import ListAltIcon from '@mui/icons-material/ListAlt';
import {generalStore, modalStore} from '../../../index';
import {administrationRoleEnum, trainingAndFieldRoleEnum, contentCreatingRoleEnum} from '../../../serverClientShared/sharedEnums';
import ConditionalComponent from '../../Shared/ConditionalComponent';
import {RenderOptionByType} from './RenderOptions';
import RoleSection from './RoleSection';
import ChooseRolePopupActions from './ChooseRolePopupActions';
import {getRoleDisplayText} from './useRoleSelection';
import {RolePresetsRequester} from '../../Domain/Groups/RolePresetsRequester';

const SaveTemplateInput = ({templateName, setTemplateName, onSave}) => (
    <div className="flex items-center gap-2">
        <input
            data-testid="save-template-name-input"
            className="flex-1 text-sm text-main-normal bg-windowsandmenus-normal border border-panels-normal rounded-input px-3 py-1.5 outline-none focus:ring-0 focus:border-classic-button-normal"
            value={templateName}
            onChange={(e) => setTemplateName(e.target.value)}
            placeholder="Template name..."
            autoFocus
        />
        <div
            data-testid="save-template-confirm"
            className={`px-3 py-1.5 rounded-button text-xs font-bold text-classic-button-normal-text ${templateName.trim() ? 'bg-classic-button-normal cursor-pointer hover:bg-classic-button-hover' : 'bg-classic-button-disabled cursor-default'}`}
            onClick={onSave}
        >
            Save
        </div>
    </div>
);

const ManualRoleView = ({
    training, content, admin, getSelection, setSelection,
    showAdministration, isFilterMode, showPresetActions, config,
    onSwitchToPresets, onUseTemplate, onClose,
}) => {
    const isSingleSelect = config.singleSelect || false;
    const [showPermissions, setShowPermissions] = useState(false);
    const [showTemplateInput, setShowTemplateInput] = useState(false);
    const [templateName, setTemplateName] = useState('');
    const [isOptionBoxOpen, setIsOptionBoxOpen] = useState(false);
    const [optionBoxPosition, setOptionBoxPosition] = useState({x: 0, y: 0});
    const [optionBoxType, setOptionBoxType] = useState('trainingAndField');

    const handleDropdownClick = (e, type) => {
        const parentRect = e.currentTarget.offsetParent.getBoundingClientRect();
        const targetRect = e.currentTarget.getBoundingClientRect();
        let x = targetRect.left - parentRect.left;
        let y = targetRect.bottom - parentRect.top;
        const popupHeight = type === 'contentCreating' ? 200 : type === 'administration' ? 150 : 400;
        if (window.innerHeight - targetRect.bottom < popupHeight) y = targetRect.top - parentRect.top - popupHeight;
        setOptionBoxType(type);
        setOptionBoxPosition({x, y});
        setIsOptionBoxOpen(true);
    };

    const handleOptionSelect = (type, option) => {
        const shouldClose = setSelection(type, option);
        if (shouldClose) setIsOptionBoxOpen(false);
    };

    const handleSave = async () => {
        if (!modalStore.modalData.isBatch && (training.size === 0 || content.size === 0) && !(training.size === 0 && content.size === 0)) {
            modalStore.showErrorMessage(generalStore.translate('PleaseSelectRolesForBothCategoriesOrNone.'));
            return;
        }
        if (modalStore.modalData.isBatch || (training.size !== 0 && content.size !== 0)) {
            await modalStore.handleSaveRolePopup(
                Array.from(training),
                Array.from(content),
                showAdministration ? Array.from(admin) : undefined,
            );
        }
        onClose();
    };

    const handleSaveTemplate = async () => {
        if (!templateName.trim()) return;
        try {
            await RolePresetsRequester.createRolePreset(generalStore.workspace, {
                name: templateName.trim(),
                TrainingAndFieldRole: Array.from(training)[0] || trainingAndFieldRoleEnum.NONE,
                ContentCreationRole: Array.from(content)[0] || contentCreatingRoleEnum.NONE,
                AdministrationRole: Array.from(admin)[0] || administrationRoleEnum.NONE,
            });
            modalStore.showSuccessMessage?.('Template saved');
            setShowTemplateInput(false);
            setTemplateName('');
        } catch (error) {
            console.error('Failed to save template:', error);
            modalStore.showErrorMessage('Failed to save template.');
        }
    };

    return (
        <>
            <div className="flex items-center justify-between">
                <span className="text-sm font-semibold">
                    {isFilterMode ? generalStore.translate('filterByRoles') || 'Filter by Roles' : generalStore.translate('assignRoles') || 'Assign Roles'}
                </span>
                <div className="flex items-center gap-1">
                    {(showPresetActions || (isFilterMode && config.onFilterApply)) && (
                        <ListAltIcon data-testid="switch-to-presets" style={{fontSize: 18}} className="text-link-normal cursor-pointer hover:text-main-normal" onClick={onSwitchToPresets} />
                    )}
                    <CloseIcon style={{fontSize: 18}} className="text-main-light cursor-pointer hover:text-main-normal" onClick={onClose} />
                </div>
            </div>

            <ConditionalComponent if={showPresetActions}>
                <div className="flex gap-2">
                    <div data-testid="use-template-button" className="flex items-center gap-1 px-3 py-1.5 rounded-button text-xs font-medium border border-panels-normal cursor-pointer hover:bg-panels-normal" onClick={onUseTemplate}>
                        Use Template
                    </div>
                    <div data-testid="save-template-button" className="flex items-center gap-1 px-3 py-1.5 rounded-button text-xs font-medium border border-panels-normal cursor-pointer hover:bg-panels-normal" onClick={() => setShowTemplateInput(!showTemplateInput)}>
                        + Save Template
                    </div>
                </div>
            </ConditionalComponent>

            <ConditionalComponent if={showTemplateInput}>
                <SaveTemplateInput templateName={templateName} setTemplateName={setTemplateName} onSave={handleSaveTemplate} />
            </ConditionalComponent>

            <div className="flex flex-col gap-2">
                <RoleSection label={generalStore.translate('TrainingAndField') || 'Training and field'} displayText={getRoleDisplayText(training, 6, 'none', isFilterMode)} type="trainingAndField" onClick={handleDropdownClick} />
                <RoleSection label={generalStore.translate('ContentCreation') || 'Content creation'} displayText={getRoleDisplayText(content, 2, 'none', isFilterMode)} type="contentCreating" onClick={handleDropdownClick} />
                <ConditionalComponent if={showAdministration}>
                    <RoleSection label={generalStore.translate('administration') || 'Administration'} displayText={getRoleDisplayText(admin, isFilterMode ? 4 : 2, 'none', isFilterMode)} type="administration" onClick={handleDropdownClick} />
                </ConditionalComponent>
            </div>

            <ConditionalComponent if={showPresetActions}>
                <ChooseRolePopupActions config={config} onViewPermissions={() => setShowPermissions(true)} onClose={onClose} />
            </ConditionalComponent>

            <div className="flex items-center justify-end gap-2 pt-1" style={{borderTop: '1px solid var(--panels-normal-color)'}}>
                <div className="px-3 py-1.5 rounded-button text-sm font-medium text-main-normal cursor-pointer hover:bg-panels-normal" onClick={onClose}>Cancel</div>
                <div data-testid="role-popup-save" className="px-3 py-1.5 rounded-button text-sm font-medium text-classic-button-normal-text bg-classic-button-normal cursor-pointer hover:bg-classic-button-hover" onClick={handleSave}>Save</div>
            </div>

            <ConditionalComponent if={isOptionBoxOpen}>
                <RenderOptionByType type={optionBoxType} position={optionBoxPosition} setSelection={handleOptionSelect} setIsOptionBoxOpen={setIsOptionBoxOpen} selectedOptions={getSelection} singleSelect={isSingleSelect || (optionBoxType === 'administration' && !isFilterMode)} isFilterMode={isFilterMode} />
            </ConditionalComponent>

            <PermissionsModal
                open={showPermissions}
                title={`${generalStore.translate('permissions') || 'Permissions'}`}
                trainingRole={Array.from(training)[0] || trainingAndFieldRoleEnum.NONE}
                contentRole={Array.from(content)[0] || contentCreatingRoleEnum.NONE}
                adminRole={Array.from(admin)[0] || administrationRoleEnum.NONE}
                onClose={() => setShowPermissions(false)}
            />
        </>
    );
};

export default ManualRoleView;
